import { useEffect, useState } from 'react';
import {
  Sparkles,
  Feather,
  CalendarClock,
  ShieldCheck,
  Loader2,
  AlertCircle,
  Check,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/contexts/PermissionContext';
import { cn } from '@/lib/utils';
import apiClient from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';

type Mode = 'LEGGERA' | 'PIANIFICATA' | 'RIGOROSA' | 'AUTO';

interface Resolved {
  effective: Exclude<Mode, 'AUTO'>;
  configured: Mode;
  auto: boolean;
  detected_at: string | null;
  params: {
    pre_shift_tolerance_minutes: number;
    absence_grace_minutes: number;
    late_clockin_blocks_booking_minutes: number;
    allow_walk_in_substitution: boolean;
    rigorosa_clockin_threshold_pct: number;
  };
}

const MODE_META: Record<Mode, { label: string; description: string; icon: typeof Sparkles }> = {
  AUTO: {
    label: 'Auto',
    description:
      'Il sistema sceglie da solo la modalità più adatta in base ai dati HR (rivalutata ogni notte).',
    icon: Sparkles,
  },
  LEGGERA: {
    label: 'Leggera',
    description: 'Solo skill, orari standard, eccezioni. Adatta a chi non usa i turni HR.',
    icon: Feather,
  },
  PIANIFICATA: {
    label: 'Pianificata',
    description:
      'Aggiunge i turni e le richieste HR approvate (ferie, permessi). Niente timbrature.',
    icon: CalendarClock,
  },
  RIGOROSA: {
    label: 'Rigorosa',
    description: 'Stato di timbratura in tempo reale + proposte manuali per gestire le assenze.',
    icon: ShieldCheck,
  },
};

const MODES: Mode[] = ['AUTO', 'LEGGERA', 'PIANIFICATA', 'RIGOROSA'];

export default function AvailabilityModeSettings() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('calendar.manage');
  const [data, setData] = useState<Resolved | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    setLoadError(null);
    apiClient
      .get<Resolved>('/calendar/availability-mode')
      .then(r => {
        if (r.success && r.data) {
          setData(r.data);
        } else {
          setLoadError(r.error || 'Risposta non valida dal server');
        }
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : undefined;
        setLoadError(msg || 'Impossibile caricare la modalità di disponibilità');
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (mode: Mode) => {
    if (!canManage) return;
    if (!data || saving || data.configured === mode) return;
    setSaving(true);
    try {
      const r = await apiClient.put<Resolved>('/calendar/availability-mode', {
        mode,
        params: data.params,
      });
      if (r.success && r.data) setData(r.data);
      toast({ title: 'Modalità aggiornata', description: `Ora attiva: ${MODE_META[mode].label}.` });
    } catch (e) {
      toast({
        title: 'Errore',
        description: e instanceof Error ? e.message : 'Impossibile salvare la modalità',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const updateParam = async (patch: Partial<Resolved['params']>) => {
    if (!canManage) return;
    if (!data) return;
    setSaving(true);
    try {
      const r = await apiClient.put<Resolved>('/calendar/availability-mode', {
        mode: data.configured,
        params: { ...data.params, ...patch },
      });
      if (r.success && r.data) setData(r.data);
    } catch (e) {
      toast({
        title: 'Errore',
        description: e instanceof Error ? e.message : 'Impossibile salvare i parametri',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-6">
        <Loader2 className="h-4 w-4 animate-spin" />
        Caricamento…
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive text-base">
            <AlertCircle className="h-5 w-5" />
            Impossibile caricare la modalità di disponibilità
          </CardTitle>
          <CardDescription>{loadError || 'Risposta vuota dal server'}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={load}>
            Riprova
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {!canManage && <p className="text-sm text-muted-foreground">Sola lettura: la modifica richiede il permesso di gestione Calendario.</p>}
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-foreground">Modalità di disponibilità</h3>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Controlla quali vincoli applica il sistema quando si verifica se una risorsa può essere
            prenotata.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saving && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Salvataggio…
            </span>
          )}
          {data.auto && (
            <Badge variant="secondary" className="gap-1">
              <Sparkles className="h-3 w-3" />
              Effettiva: {MODE_META[data.effective].label}
            </Badge>
          )}
        </div>
      </div>

      {/* Modes */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h4 className="text-sm font-semibold text-foreground">Modalità</h4>
          <span className="text-xs text-muted-foreground">
            Seleziona come vengono valutate le disponibilità
          </span>
        </div>
        <div
          role="radiogroup"
          aria-label="Modalità di disponibilità"
          className="grid gap-3 sm:grid-cols-2"
        >
          {MODES.map(m => {
            const meta = MODE_META[m];
            const Icon = meta.icon;
            const selected = data.configured === m;
            const isEffective = data.auto && m !== 'AUTO' && data.effective === m;
            return (
              <button
                key={m}
                type="button"
                role="radio"
                disabled={!canManage || saving}
                aria-checked={selected}
                onClick={() => save(m)}
                className={cn(
                  'group relative text-left rounded-lg border p-4 transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                  selected
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border bg-card hover:border-primary/40 hover:bg-accent/40',
                )}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors',
                      selected
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground">{meta.label}</span>
                      {isEffective && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4">
                          modalità effettiva
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {meta.description}
                    </p>
                  </div>
                  {selected && (
                    <span
                      aria-hidden
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
                    >
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <Separator />

      {/* Parameters */}
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Parametri</h4>
          <p className="text-xs text-muted-foreground">
            Soglie di tolleranza usate dal motore di disponibilità.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <NumberParam
            id="pre-shift"
            label="Tolleranza pre-turno"
            unit="min"
            helper="Minuti prima dell'inizio turno in cui la risorsa è già prenotabile."
            value={data.params.pre_shift_tolerance_minutes}
            disabled={saving || !canManage}
            onChange={v => updateParam({ pre_shift_tolerance_minutes: v })}
          />
          <NumberParam
            id="grace"
            label="Tolleranza per assenza"
            unit="min"
            helper="Minuti di tolleranza prima di considerare una risorsa assente."
            value={data.params.absence_grace_minutes}
            disabled={saving || !canManage}
            onChange={v => updateParam({ absence_grace_minutes: v })}
          />
          <NumberParam
            id="threshold"
            label="Soglia % timbratura"
            unit="%"
            helper="Soglia per attivare RIGOROSA in modalità AUTO."
            value={data.params.rigorosa_clockin_threshold_pct}
            disabled={saving || !canManage}
            min={0}
            max={100}
            onChange={v => updateParam({ rigorosa_clockin_threshold_pct: v })}
          />
        </div>
      </div>

      <Separator />

      {/* Options */}
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Opzioni</h4>
          <p className="text-xs text-muted-foreground">
            Comportamenti del motore di disponibilità.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card divide-y divide-border">
          <SwitchRow
            id="walk-in"
            label="Permetti sostituzioni walk-in"
            description="Consenti l'assegnazione di una risorsa libera a un cliente che si presenta senza prenotazione."
            checked={data.params.allow_walk_in_substitution}
            disabled={saving || !canManage}
            onChange={v => updateParam({ allow_walk_in_substitution: v })}
          />
        </div>
      </div>
    </div>
  );
}

function NumberParam({
  id,
  label,
  unit,
  helper,
  value,
  onChange,
  disabled,
  min = 0,
  max,
}: {
  id: string;
  label: string;
  unit: string;
  helper?: string;
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type="number"
          min={min}
          max={max}
          value={value}
          disabled={disabled}
          onChange={e => onChange(Number(e.target.value))}
          className="pr-12"
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-medium text-muted-foreground">
          {unit}
        </span>
      </div>
      {helper && <p className="text-[11px] text-muted-foreground leading-snug">{helper}</p>}
    </div>
  );
}

function SwitchRow({
  id,
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 p-4">
      <div className="min-w-0 flex-1 space-y-0.5">
        <Label htmlFor={id} className="text-sm font-medium text-foreground cursor-pointer">
          {label}
        </Label>
        {description && (
          <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
        )}
      </div>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}
